import { useState, useEffect, useRef } from "react";
import { Network, Radio, Brain, FileJson, Wifi, WifiOff, Play, AlertCircle } from "lucide-react";
import { getWsUrl, API_BASE } from "@/lib/api";

interface AgentLog {
  agent: string;
  task: string;
  thought: string;
  timestamp: number;
  step: number;
}

export default function CrewDashboardPage() {
  const [logs, setLogs] = useState<AgentLog[]>([]);
  const [connected, setConnected] = useState(false);
  const [filter, setFilter] = useState<string>("ALL");
  const [botRunning, setBotRunning] = useState<boolean | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Check if bot is running
  const checkBotStatus = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/bot/status`);
      const data = await r.json();
      setBotRunning(data.running || false);
    } catch (e) {
      setBotRunning(false);
    }
  };

  // Start bot
  const startBot = async () => {
    try {
      await fetch(`${API_BASE}/api/bot/start?mode=hybrid`, { method: "POST" });
      checkBotStatus();
    } catch (e) {
      console.error("Failed to start bot", e);
    }
  };

  useEffect(() => {
    checkBotStatus();
    const statusInterval = setInterval(checkBotStatus, 10000);
    return () => clearInterval(statusInterval);
  }, []);

  useEffect(() => {
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (event) => {
      try {
        const data: AgentLog = JSON.parse(event.data);
        setLogs((prev) => [...prev.slice(-300), data]);
      } catch (e) {
        console.error("Failed to parse WebSocket message", e);
      }
    };

    return () => ws.close();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const filteredLogs = filter === "ALL" ? logs : logs.filter((l) => l.agent === filter);

  const agentColors: Record<string, string> = {
    Scraper: "text-cyan-400",
    Quant: "text-purple-400",
    Executor: "text-yellow-400",
  };

  const agentStats = {
    Scraper: logs.filter((l) => l.agent === "Scraper").length,
    Quant: logs.filter((l) => l.agent === "Quant").length,
    Executor: logs.filter((l) => l.agent === "Executor").length,
  };

  return (
    <div className="p-6 space-y-4 h-screen flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Network size={22} className="text-terminal-accent" />
          <div>
            <h1 className="text-xl font-bold text-white">CREW AI DASHBOARD</h1>
            <p className="text-xs text-gray-500">Live multi-agent thought stream via WebSocket</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Bot Status */}
          {botRunning === false && (
            <button
              onClick={startBot}
              className="flex items-center gap-2 px-3 py-1.5 bg-green-600/20 border border-green-600 text-green-400 rounded text-xs hover:bg-green-600/30 transition-colors"
            >
              <Play size={12} /> START BOT
            </button>
          )}
          {connected ? (
            <span className="flex items-center gap-1 text-xs text-green-400">
              <Wifi size={12} /> CONNECTED
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-red-400">
              <WifiOff size={12} /> DISCONNECTED
            </span>
          )}
        </div>
      </div>

      {/* Bot Not Running Warning */}
      {botRunning === false && logs.length === 0 && (
        <div className="flex items-center gap-3 p-3 border border-yellow-600/50 bg-yellow-600/10 rounded">
          <AlertCircle size={16} className="text-yellow-400 shrink-0" />
          <div className="text-xs text-yellow-400">
            <strong>Bot is not running.</strong> Click "START BOT" above or go to Bot Control to start the Crew AI pipeline. 
            Agents only produce thoughts when the trading cycle is active.
          </div>
          <button
            onClick={startBot}
            className="ml-auto flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-500 shrink-0"
          >
            <Play size={12} /> START
          </button>
        </div>
      )}

      {/* Agent Stats */}
      <div className="grid grid-cols-3 gap-4">
        {["Scraper", "Quant", "Executor"].map((agent) => {
          const count = agentStats[agent as keyof typeof agentStats];
          const last = logs.filter((l) => l.agent === agent).pop();
          return (
            <div 
              key={agent} 
              className="terminal-window p-3 cursor-pointer hover:border-terminal-accent/50 transition-colors" 
              onClick={() => setFilter(filter === agent ? "ALL" : agent)}
            >
              <div className="flex items-center gap-2 mb-1">
                {agent === "Scraper" && <Radio size={14} className="text-cyan-400" />}
                {agent === "Quant" && <Brain size={14} className="text-purple-400" />}
                {agent === "Executor" && <FileJson size={14} className="text-yellow-400" />}
                <span className="text-xs font-bold text-white">{agent.toUpperCase()}</span>
                <span className="ml-auto text-[10px] text-gray-500">{count} thoughts</span>
              </div>
              <div className="text-[10px] text-gray-400 truncate">
                {last ? last.thought.substring(0, 60) + "..." : "Waiting for first thought..."}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter Bar */}
      <div className="flex gap-2">
        {["ALL", "Scraper", "Quant", "Executor"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-[10px] px-2 py-1 border rounded cursor-pointer ${
              filter === f
                ? "border-terminal-accent text-terminal-accent bg-terminal-accent/10"
                : "border-terminal-border text-gray-500 hover:text-gray-300"
            }`}
          >
            {f}
          </button>
        ))}
        <span className="ml-auto text-[10px] text-gray-500">{filteredLogs.length} entries</span>
      </div>

      {/* Thought Stream */}
      <div className="terminal-window flex-1 flex flex-col overflow-hidden">
        <div className="terminal-header">
          <span className="flex items-center gap-2"><Brain size={12} /> AGENT THOUGHT STREAM</span>
        </div>
        <div className="flex-1 overflow-auto p-3 space-y-1 font-mono text-xs">
          {filteredLogs.map((log, i) => (
            <div key={i} className="flex items-start gap-2 hover:bg-white/5 p-1 rounded">
              <span className="text-gray-600 shrink-0">[{new Date(log.timestamp * 1000).toLocaleTimeString()}]</span>
              <span className={`font-bold shrink-0 ${agentColors[log.agent] || "text-gray-400"}`}>[{log.agent}]</span>
              <span className="text-gray-300">{log.thought}</span>
            </div>
          ))}
          <div ref={bottomRef} />
          {filteredLogs.length === 0 && (
            <div className="text-gray-600 text-center py-8">
              {botRunning === false 
                ? "Bot is stopped. Start the bot to see agent thoughts." 
                : botRunning === true
                ? "Bot is running. Waiting for next cycle..."
                : "Waiting for agent thoughts..."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
